import { Component, OnDestroy } from '@angular/core';
import { NgxLoggerLevel } from 'ngx-logger';
import { filter } from 'rxjs/operators';

import { AppSetting } from '@lbmx/types';
import { ConfigProvider } from './provider/config-provider';

import { AdminConfig, AdministrationService } from '@lbmx/administration';
import { TestingConfig, TestingService } from '@lbmx/edi-testing';
import { AuthConfig, AuthenticateService } from '@lbmx/phoenix-lib-auth';
import { LoggerService } from '@lbmx/phoenix-lib-core';

import { Subscription } from 'rxjs';
import { BreadcrumbService } from 'xng-breadcrumb';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnDestroy {
  public subscriptions: Subscription[] = [];

  constructor(
    private configPrv: ConfigProvider,
    private authSrv: AuthenticateService,
    private adminSrv: AdministrationService,
    private loggerSrv: LoggerService,
    private testSrv: TestingService,
    // Required to initialize the breadcrumbs
    private breadcrumbService: BreadcrumbService
  ) {
    this.configAuth();
    this.configAdmin();
    this.configTesting();
    this.configLogger();
  }

  public ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }
  }

  private configLogger() {
    this.loggerSrv.setLoggerConfig(this.getUriLoggerConfig());
  }

  private getUriLoggerConfig() {
    const appSettings: AppSetting = this.configPrv.AppSetting;

    const loggerConfig = this.loggerSrv.getLoggerConfig();

    loggerConfig.level = NgxLoggerLevel.TRACE;
    loggerConfig.serverLoggingUrl = appSettings.uriLogging.logging;
    loggerConfig.serverLogLevel = NgxLoggerLevel.TRACE;

    return loggerConfig;
  }

  private configAuth() {
    this.authSrv.configure(this.getAuthConfig());

    this.subscriptions.push(
      this.authSrv.events
        .pipe(filter((e) => e.type === 'token_received'))
        .subscribe((e) => {
          this.loggerSrv.info(
            'AppComponent.configAuth',
            'User session has started: token_received'
          );
        })
    );
  }

  private getAuthConfig(): AuthConfig {
    const appSettings: AppSetting = this.configPrv.AppSetting;

    const authConfig: AuthConfig = {
      // Url of the Identity Provider
      // issuer: 'https://steyer-identity-server.azurewebsites.net/identity',

      // URL of the SPA to redirect the user to after login
      // redirectUri: window.location.origin + '/index.html',

      // URL of the SPA to redirect the user after silent refresh
      // silentRefreshRedirectUri: window.location.origin + '/silent-refresh.html',
      tokenEndpoint: appSettings.uriSecurity.authenticate,
      userinfoEndpoint: appSettings.uriSecurity.getProfile,
      refreshTokenEndpoint: appSettings.uriSecurity.slidingSession,
      logOutEndPoint: appSettings.uriSecurity.logOut,
      rememberMeLoadEndPoint: appSettings.uriSecurity.rememberMeLoad,
      getCookieRememberMeEndPoint: appSettings.uriSecurity.getCookieRememberMe,

      // The SPA's id. The SPA is registerd with this id at the auth-server
      // clientId: 'spa-phonenix',

      // set the scope for the permissions the client should request
      // The first three are defined by OIDC. The 4th is a usecase-specific one
      // scope: 'openid profile email voucher',
    };
    return authConfig;
  }

  private configAdmin() {
    this.adminSrv.setConfig(this.getAdminConfig());
  }

  private getAdminConfig(): AdminConfig {
    const appSettings: AppSetting = this.configPrv.AppSetting;
    const adminConfig: AdminConfig = {
      searchUserEndpoint: appSettings.uriAdministration.searchUser,
      addUserEndpoint: appSettings.uriAdministration.addUser,
      updateUserEndpoint: appSettings.uriAdministration.updateUser,
      listPermissionsEndpoint: appSettings.uriAdministration.listPermissions,
      deleteUserEndpoint: appSettings.uriAdministration.deleteUser,
      languageTypesEndpoint: appSettings.uriAdministration.languageTypes,
      timeZoneTypesEndpoint: appSettings.uriConfig.getTimeZoneTypes,
      dateFormatTypesEndpoint: appSettings.uriAdministration.dateFormatTypes,
      exportUsersEndpoint: appSettings.uriAdministration.exportUsers,
      getConfigEndpoint: appSettings.uriAdministration.getConfig,
      getAccountEndpoint: appSettings.uriAdministration.getAccount,
    };
    return adminConfig;
  }

  private configTesting() {
    this.testSrv.setConfig(this.getTestingConfig());
  }

  private getTestingConfig(): TestingConfig {
    const appSettings: AppSetting = this.configPrv.AppSetting;
    const testConfig: TestingConfig = {
      getCompaniesEndpoint: appSettings.uriTesting.getCompanies,
      getTestingPartnersEndpoint: appSettings.uriTesting.getTestingPartners,
      getTestResultsEndpoint: appSettings.uriTesting.getTestResults,
      getTestDetailsEndpoint: appSettings.uriTesting.getTestDetails,
      getItemDetailsEndpoint: appSettings.uriTesting.getItemDetails,
      addItemDetailsEndpoint: appSettings.uriTesting.addItemDetails,
      getUnitOfMeasuresEndpoint: appSettings.uriTesting.getUnitOfMeasures,
      getCustomerEndpoint: appSettings.uriTesting.getSetupDetails,
      getCountriesEndpoint: appSettings.uriConfig.getCountries,
      getProvincesEndpoint: appSettings.uriConfig.getProvinces,
      testingPartnerSurveyEndpoint: appSettings.uriTesting.testingPartnerSurvey,
      getGroupTradingPartnersEndpoint: appSettings.uriTesting.getGroupTradingPartners,
      getEdiCustomerInfoEndpoint: appSettings.uriTesting.getEdiCustomerInfo,
      getAddressAutocompleteEndpoint: appSettings.uriTesting.getAddressAutocomplete,
      getPurchaseOrderEndpoint: appSettings.uriTesting.getPurchaseOrder,
      getVideoStreamEndpoint: appSettings.uriTesting.getVideoStream
    };
    return testConfig;
  }

  //#endregion
}
